// components/TableToolbar.tsx
import React, { useRef, useState } from 'react';
import { Plus, Share2 } from 'lucide-react';
import { ProcessingStatus } from './ProcessingStatus';
import { FilterSelect } from './table-filters/FilterSelect';
import { ActiveFilters } from './table-filters/ActiveFilters';
import { useTableFilters, DEFAULT_FILTERS } from '../hooks/useTableFilters';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { useDeleteProject, useUserProfile } from '@/core/api/queries';
import { useCompanyProject } from '../hooks/useCompanyProject';
import { useNavigate } from 'react-router-dom';

interface TableToolbarProps {
  projectId: string | null;
  onFileUpload: (file: File) => Promise<void>;
  onShareClick: () => void;
}

const TableToolbar: React.FC<TableToolbarProps> = ({ projectId, onFileUpload, onShareClick }) => {
  const [deleteProjectModalOpened, setDeleteProjectModalOpened] = useState(false)
  const { data: userProfile } = useUserProfile()
  const fileInputRef = useRef<HTMLInputElement>(null);
  const deleteProject = useDeleteProject()
  const navigate = useNavigate()

  const { selectedFilters, handleFilterChange, handleClearAll, handleRemoveFilter, selectedFilterCount } =
    useTableFilters();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await onFileUpload(file);
      event.target.value = ''; // Reset input
    }
  };

  return (
    <div className="mb-4 bg-white border border-gray-200 relative z-[1100]">
      <div className="p-4 flex items-center">
        <div className="flex w-full items-center space-x-4 justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600 font-medium">Quick filters:</span>
            {DEFAULT_FILTERS.map((filter) => (
              <FilterSelect
                key={filter.fieldKey}
                label={filter.label}
                fieldKey={filter.fieldKey}
                onChange={handleFilterChange}
                projectId={projectId}
                selectedValues={
                  selectedFilters.filter((f) => f.fieldKey === filter.fieldKey).map((f) => f.optionValue) || []
                }
                selectedFilterCount={selectedFilterCount(filter.fieldKey)}
              />
            ))}
          </div>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
              className="hidden"
            />
            {projectId && <ProcessingStatus projectId={projectId} />}
            <ConfirmationModal
              isOpen={deleteProjectModalOpened}
              onClose={() => setDeleteProjectModalOpened(false)}
              onConfirm={() => {
                deleteProject.mutateAsync(projectId as string)
                navigate('/app/onboarding')
              }}
              title="Delete Shipment"
              message={`Are you sure you want to delete this project?\n\nThis action cannot be undone and all associated data will be permanently removed.`}
              confirmText="Delete"
              cancelText="Cancel"
              variant="danger"
            />

            {['super_admin', 'admin'].includes(userProfile?.role) &&
              <button
                onClick={() => setDeleteProjectModalOpened(true)}
                className="bg-red-600 text-white hover:bg-red-700 rounded-md transition-colors flex items-center justify-center gap-2 px-[16px] py-[8px] "
                title="Remove project"
              >
                <Plus className="w-4 h-4" />
                Remove project
              </button>
            }
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors flex items-center justify-center gap-2 px-[16px] py-[8px] "
              title="Add more"
            >
              <Plus className="w-4 h-4" />
              Add more
            </button>
            <button
              onClick={onShareClick}
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors flex items-center justify-center gap-2 px-[16px] py-[8px]"
              title="Share Data"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>
      </div>
      <ActiveFilters filters={selectedFilters} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAll} />
    </div>
  );
};

export default TableToolbar;
